"use client";

import { Box, Flex, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import { LuArrowRight } from "react-icons/lu";
import NextLink from "next/link";

import { Reveal } from "@components/common/Reveal";
import { HOW_IT_WORKS_STEPS } from "@features/home/howItWorksSteps";

const StepCard = ({
  step,
  index,
}: {
  step: (typeof HOW_IT_WORKS_STEPS)[number];
  index: number;
}) => {
  const Icon = step.icon;
  return (
    <Flex
      direction="column"
      gap={3}
      p={6}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.light"
      bg="sidebar.background"
      _hover={{ borderColor: "border.dark" }}
      transition="border-color 0.2s"
    >
      <Flex justify="space-between" align="flex-start">
        <Text
          fontSize="xs"
          fontWeight="bold"
          color="text.secondary"
          fontFamily="mono"
        >
          {String(index + 1).padStart(2, "0")}
        </Text>
        <Box as={Icon} boxSize={5} color="fg" opacity={0.7} aria-hidden />
      </Flex>
      <Heading size="sm" color="fg">
        {step.title}
      </Heading>
      <Text fontSize="sm" color="text.secondary" lineHeight="tall">
        {step.summary}
      </Text>
    </Flex>
  );
};

export const HowItWorksSection = () => {
  return (
    <Box
      id="how-it-works"
      as="section"
      py={{ base: 16, md: 20 }}
      px={{ base: 6, md: 12, lg: 16 }}
      bg="surface.background"
    >
      <Box maxW="7xl" mx="auto">
        <Reveal>
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "flex-end" }}
            direction={{ base: "column", md: "row" }}
            gap={4}
            mb={12}
          >
            <Flex direction="column" gap={4}>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="text.secondary"
                bg="sidebar.hover"
                px={3}
                py={1.5}
                borderRadius="full"
                w="fit-content"
              >
                How it works
              </Text>
              <Heading size="xl" color="fg" maxW="lg">
                From a rate quote to your door, in six steps
              </Heading>
            </Flex>
            <NextLink href="/how-it-works">
              <Flex
                align="center"
                gap={1.5}
                fontSize="sm"
                fontWeight="semibold"
                color="fg"
                _hover={{ color: "text.secondary" }}
              >
                See full details <LuArrowRight />
              </Flex>
            </NextLink>
          </Flex>
        </Reveal>

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={5}>
          {HOW_IT_WORKS_STEPS.map((step, i) => (
            <Reveal key={step.id} delay={i * 80}>
              <StepCard step={step} index={i} />
            </Reveal>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
};
